import { css } from "@linaria/core";
import cx from "classnames";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { colors } from "@ui/Theme";
import { useDashboardUnsaved } from "../../../../../../hook/useDashboardUnsaved";

const editableFieldContainer = css`
  display: grid;

  &::before,
  textarea {
    grid-area: 1 / 1 / 2 / 2;
    max-width: 740px;
  }

  &::before {
    content: attr(data-content) " ";
    white-space: pre-wrap;
    visibility: hidden;
  }

  textarea {
    border: none;
    height: 100%;
    overflow: hidden;
    padding: 4px;
    resize: none;
    width: 100%;
    font: inherit;
    word-wrap: break-word;
    color: ${colors.carbon800};
    background-color: inherit;

    &::placeholder {
      color: ${colors.carbon500};
    }
  }
`;

export const SimpleInlineTextEditor = ({
  dashboardId,
  className,
  ignoreLikeBreaks = false,
  initialValue,
  onUpdate,
  placehoder = "Insert text here",
  initialFocus = false,
}: {
  dashboardId: string;
  className?: string;
  ignoreLikeBreaks?: boolean;
  initialValue: string;
  onUpdate?: (value: string) => void;
  placehoder?: string;
  initialFocus?: boolean;
}) => {
  const initialValueRef = useRef(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(initialValue);
  const [, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });
  const initialRows = useMemo(() => initialValue.split("\n").length || 1, [initialValue]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setUnsaved(e.target.value.trim() !== initialValueRef.current.trim());
      setValue(e.target.value);
      onUpdate?.(e.target.value);
    },
    [onUpdate, setUnsaved],
  );

  const ignoreLineBreaksHandler = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => e.key === "Enter" && e.preventDefault(),
    [],
  );

  useLayoutEffect(() => {
    if (textareaRef.current && initialFocus) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length,
      );
    }
  }, [initialFocus]);

  return (
    <div className={cx(editableFieldContainer, className)} data-content={value}>
      <textarea
        ref={textareaRef}
        onKeyDown={ignoreLikeBreaks ? ignoreLineBreaksHandler : undefined}
        placeholder={placehoder}
        value={value}
        onChange={onChange}
        rows={initialRows}
        maxLength={3000}
      />
    </div>
  );
};
